import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Save, Plus, Trash, AlertCircle, ChevronLeftIcon, CheckSquare, Square } from "lucide-react";
import { Select } from "antd";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import {
    APPRAISAL_CYCLES,
    APPRAISAL_KPI_METRICS,
    APPRAISALS,
    SUBORDINATES_URL,
} from "../../constants/api";

import useAppraisalRole, { useAppraisalPermissions } from "../../hooks/useAppraisalRole";

const TeamReviewAssign = () => {
    const navigate = useNavigate();
    const { fetch, create } = useApiMethods();
    const { isHR } = useAppraisalRole();
    const { canAssignAppraisal } = useAppraisalPermissions();

    const [cycles, setCycles] = useState([]);
    const [subordinates, setSubordinates] = useState([]);
    const [kpis, setKpis] = useState([]);
    const [loadingMeta, setLoadingMeta] = useState(true);

    const [cycleId, setCycleId] = useState(null);
    const [employeeId, setEmployeeId] = useState(null);
    const [goals, setGoals] = useState([]);
    const [pickerKpis, setPickerKpis] = useState([]);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    console.log("subordinates", subordinates)

    // HR sees everyone; RM sees only their subordinates. Both endpoints return
    // shapes we can normalize for the Select component.
    const loadMeta = useCallback(async () => {
        setLoadingMeta(true);
        try {
            const [cycleRes, kpiRes, subRes] = await Promise.all([
                fetch(APPRAISAL_CYCLES, { status: "active" }),
                fetch(APPRAISAL_KPI_METRICS),
                fetch(SUBORDINATES_URL, { dropdown: true }),
            ]);
            const cycleList = cycleRes?.data || [];
            setCycles(cycleList);
            setKpis(kpiRes?.data || []);
            const subList = (subRes?.data || []).map((e) => ({
                id: e.employee?.id || e.id,

                name:
                    e.employee?.employee_name ||
                    `${e.first_name || ""} ${e.last_name || ""}`.trim(),

                designation:
                    e.employee?.designation?.name ||
                    e.designation ||
                    "",

                designation_id:
                    e.employee?.designation?.id || null,

                department_id:
                    e.employee?.department?.id || null,

                department_name:
                    e.employee?.department?.name ||
                    e.department ||
                    "",

                employee_id:
                    e.employee?.employee_id || "",

                email:
                    e.email_id || "",

                doj:
                    e.date_of_joining || "",

                is_active:
                    e.is_active ?? true,
            }));
            setSubordinates(subList);
            // Default to the active cycle if there's exactly one.
            if (cycleList.length && !cycleId) setCycleId(cycleList[0].id);
        } catch (_) {
            ToastMsg("error", "Failed to load setup data");
        } finally {
            setLoadingMeta(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadMeta(); }, [loadMeta]);

    const selectedKpiIds = new Set(goals.map((g) => g.kpi_id));
    const employeeDepartmentId = subordinates.find((e) => e.id === employeeId)?.department_id;

    const relevantKpis = useMemo(() => {
        // KPIs now carry an array of department_ids (M2M). A KPI is relevant
        // if it has no departments tagged (universal) OR the employee's
        // department is in the KPI's department list.
        return kpis
            .filter((k) => !selectedKpiIds.has(k.id))
            .filter((k) => {
                const ids = Array.isArray(k.department_ids) ? k.department_ids : [];
                if (!employeeDepartmentId) return true;
                if (ids.length === 0) return true;        // universal
                return ids.includes(employeeDepartmentId);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kpis, goals, employeeDepartmentId]);

    const totalWeight = goals.reduce((sum, g) => sum + (Number(g.weightage) || 0), 0);

    useEffect(() => {
        // Reset goals when employee changes since KPI relevance differs by dept.
        setGoals([]);
        setPickerKpis([]);
    }, [employeeId]);

    if (!canAssignAppraisal) {
        return (
            <ContainerCard scrollable>
                <ModuleTitleCard Icon={UserPlus} title="Assign Appraisal" subtitle="Reporting Managers and HR only" />
                <div className="text-center py-12 text-muted-foreground">
                    You don't have permission to assign appraisals.
                </div>
            </ContainerCard>
        );
    }

    const addKpisAsGoals = () => {
        if (!pickerKpis.length) return;
        const existingIds = new Set(goals.map((g) => g.kpi_id));
        const toAdd = pickerKpis
            .map((id) => kpis.find((k) => k.id === id))
            .filter((kpi) => kpi && !existingIds.has(kpi.id))
            .map((kpi) => ({
                kpi_id: kpi.id,
                kpi_name: kpi.name,
                weightage: kpi.default_weightage ?? 0,
                expected_outcome: "",
            }));
        if (!toAdd.length) {
            setPickerKpis([]);
            return;
        }
        setGoals((g) => [...g, ...toAdd]);
        setPickerKpis([]);
        if (errors.goals) setErrors((e) => ({ ...e, goals: undefined }));
    };

    const updateGoal = (kpi_id, patch) => {
        setGoals((gs) => gs.map((g) => (g.kpi_id === kpi_id ? { ...g, ...patch } : g)));
    };

    const removeGoal = (kpi_id) => {
        setGoals((gs) => gs.filter((g) => g.kpi_id !== kpi_id));
    };

    const validate = () => {
        const e = {};
        if (!cycleId) e.cycleId = "Pick an appraisal cycle.";
        if (!employeeId) e.employeeId = "Pick an employee.";
        if (goals.length === 0) e.goals = "Add at least one KPI.";
        if (goals.length > 0 && Math.abs(totalWeight - 100) > 0.01) {
            e.weightage = `Weightages must total 100%. Current total: ${totalWeight}%.`;
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) {
            ToastMsg("error", "Please fix the highlighted issues");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                cycle: cycleId,
                employee: employeeId,
                goals: goals.map((g) => ({
                    kpi_id: g.kpi_id,
                    weightage: Number(g.weightage) || 0,
                    expected_outcome: g.expected_outcome || "",
                })),
            };
            const res = await create(APPRAISALS, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Appraisal assigned");
                navigate(`/appraisal/team-review/${res.data?.id}`);
            } else {
                ToastMsg("error", res?.message || "Failed to assign");
            }
        } catch (_) {
            ToastMsg("error", "Failed to assign");
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        navigate("/appraisal/team-review");
    }

    return (
        <ContainerCard scrollable>
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeftIcon className="h-5 w-5 cursor-pointer" onClick={handleBack} />
                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Assign Appraisal
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Pick a cycle, pick the employee, and define the KPIs and weightages
                    </p>
                </div>
            </div>

            <div className="px-3 pb-6 space-y-4">
                {loadingMeta ? (
                    <div className="text-center py-10 text-muted-foreground">Loading…</div>
                ) : (
                    <>
                        {/* Step 1: Cycle + Employee */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5 space-y-4">
                            <h2 className="text-base font-semibold">1. Cycle & Employee</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Appraisal Cycle <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        value={cycleId}
                                        onChange={(v) => { setCycleId(v); if (errors.cycleId) setErrors((e) => ({ ...e, cycleId: undefined })); }}
                                        options={cycles.map((c) => ({
                                            value: c.id,
                                            label: `${c.name} (${dayjs(c.period_start).format("MMM YYYY")} – ${dayjs(c.period_end).format("MMM YYYY")})`,
                                        }))}
                                        className="w-full"
                                        size="large"
                                        placeholder="Select a cycle"
                                    />
                                    {errors.cycleId && <p className="text-xs text-red-500 mt-1">{errors.cycleId}</p>}
                                    {cycles.length === 0 && (
                                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                            No active cycles. Ask HR to create one.
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Employee <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        value={employeeId}
                                        onChange={(v) => { setEmployeeId(v); if (errors.employeeId) setErrors((e) => ({ ...e, employeeId: undefined })); }}
                                        options={subordinates.map((e) => ({
                                            value: e.id,
                                            label: e.designation ? `${e.name} · ${e.designation}` : e.name,
                                        }))}
                                        className="w-full"
                                        size="large"
                                        placeholder={isHR ? "Pick any employee" : "Pick from your team"}
                                        showSearch
                                        optionFilterProp="label"
                                    />
                                    {errors.employeeId && <p className="text-xs text-red-500 mt-1">{errors.employeeId}</p>}
                                    {subordinates.length === 0 && (
                                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                            No subordinates configured for you.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Step 2: KPIs */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5 space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <h2 className="text-base font-semibold">2. KPIs & Weightages</h2>
                                <span className={cn(
                                    "text-xs font-medium px-2 py-0.5 rounded-full self-start sm:self-auto",
                                    Math.abs(totalWeight - 100) < 0.01
                                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                        : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                )}>
                                    Total: {totalWeight}% / 100%
                                </span>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-2">
                                <Select
                                    mode="multiple"
                                    value={pickerKpis}
                                    onChange={(v) => setPickerKpis(v)}
                                    // Plain-text label keeps the selected tag chips clean inside
                                    // the input. `optionRender` below paints the explicit
                                    // checkbox UI for each row in the dropdown.
                                    options={relevantKpis.map((k) => {
                                        const deptStr = Array.isArray(k.department_names) && k.department_names.length
                                            ? ` · ${k.department_names.join(", ")}`
                                            : "";
                                        return {
                                            value: k.id,
                                            label: `${k.name} (${k.default_weightage}%) · ${k.category}${deptStr}`,
                                        };
                                    })}
                                    optionRender={(oriOption) => {
                                        const checked = pickerKpis.includes(oriOption.value);
                                        const Icon = checked ? CheckSquare : Square;
                                        return (
                                            <div className="flex items-center gap-2">
                                                <Icon className={cn(
                                                    "w-4 h-4 flex-shrink-0",
                                                    checked ? "text-sidebar-primary" : "text-gray-300 dark:text-gray-500",
                                                )} />
                                                <span className="truncate">{oriOption.label}</span>
                                            </div>
                                        );
                                    }}
                                    placeholder={!employeeId ? "Pick an employee first" : relevantKpis.length === 0 ? "All applicable KPIs added" : "Select one or more KPIs to add"}
                                    disabled={!employeeId || relevantKpis.length === 0}
                                    className="flex-1"
                                    size="large"
                                    showSearch
                                    optionFilterProp="label"
                                    maxTagCount="responsive"
                                    allowClear
                                    menuItemSelectedIcon={null}
                                />
                                <Button
                                    onClick={addKpisAsGoals}
                                    disabled={pickerKpis.length === 0}
                                    variant="brand"
                                >
                                    <Plus className="w-4 h-4 mr-1" /> Add {pickerKpis.length > 0 ? `${pickerKpis.length} KPI${pickerKpis.length > 1 ? "s" : ""}` : "KPI"}
                                </Button>
                            </div>

                            {errors.goals && <p className="text-xs text-red-500">{errors.goals}</p>}
                            {errors.weightage && (
                                <div className="flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-2 text-xs text-amber-700 dark:text-amber-300">
                                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                    <span>{errors.weightage}</span>
                                </div>
                            )}

                            {goals.length === 0 ? (
                                <div className="text-center py-6 text-sm text-muted-foreground border border-dashed border-gray-300 dark:border-gray-600 rounded-md">
                                    No KPIs added yet. Pick one above to begin.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {goals.map((g) => (
                                        <div key={g.kpi_id} className="rounded-md border border-gray-200 dark:border-gray-700 p-3 sm:p-4 space-y-3 bg-gray-50/50 dark:bg-gray-900/30">
                                            <div className="flex items-start justify-between gap-2">
                                                <h3 className="text-sm font-semibold">{g.kpi_name}</h3>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => removeGoal(g.kpi_id)}
                                                    className="text-red-500 hover:text-red-600 -mt-1 -mr-1"
                                                    title="Remove KPI"
                                                >
                                                    <Trash className="w-4 h-4" />
                                                </Button>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                <div className="sm:col-span-1">
                                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Weightage (%)</label>
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        value={g.weightage ?? ""}
                                                        onChange={(e) => updateGoal(g.kpi_id, { weightage: Number(e.target.value) || 0 })}
                                                    />
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Expected Outcome</label>
                                                    <Textarea
                                                        rows={2}
                                                        value={g.expected_outcome || ""}
                                                        placeholder="What does success look like for this KPI?"
                                                        onChange={(e) => updateGoal(g.kpi_id, { expected_outcome: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sticky bottom-0 bg-gradient-to-t from-white dark:from-gray-900 from-50% to-transparent pt-3 -mx-3 px-3 pb-1">
                            <Button variant="outline" onClick={() => navigate("/appraisal/team-review")}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                variant="brand"
                            >
                                <Save className="w-4 h-4 mr-1" /> {saving ? "Assigning…" : "Assign Appraisal"}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </ContainerCard>
    );
};

export default TeamReviewAssign;
